import type { Page } from '@setplex/tria-api'

type CacheEntry = {
  expiresAt: number
  page: Page<unknown>
}

const cache = new Map<string, CacheEntry>()

const clone = <T>(value: T): T => {
  if (typeof structuredClone === 'function') return structuredClone(value)
  return JSON.parse(JSON.stringify(value)) as T
}

export function firstPage<T>(
  page: Page<T>,
  sort: (a: T, b: T) => number
): Page<T> {
  return {
    ...page,
    content: [...page.content].sort(sort).slice(0, 1),
  }
}

export function bySortOrder(
  a: { sortOrder?: number },
  b: { sortOrder?: number }
): number {
  return Number(a?.sortOrder) - Number(b?.sortOrder)
}

/**
 * Player launch asks for `page: 0, count: 1` with `sortBy: 'sortOrder'`.
 * Show pages already fetched a complete first page (default count, any sort),
 * so the probe can reuse that page instead of another request.
 *
 * Transport `subscriberCache` only matches exact URLs, so this sits beside it.
 */
export function reuseFirstBySortOrder<T extends { sortOrder?: number }>(
  key: string,
  page: number,
  count: number | undefined
): Page<T> | null {
  if (page !== 0 || count !== 1) return null

  const entry = cache.get(key)
  if (!entry || entry.expiresAt <= Date.now()) return null

  return firstPage(clone(entry.page as Page<T>), bySortOrder)
}

export function rememberCompletePage<T>(
  key: string,
  page: number,
  pageData: Page<T>,
  ttl: number
): void {
  if (page !== 0 || pageData.totalPages > 1) return

  cache.set(key, {
    expiresAt: Date.now() + ttl,
    page: clone(pageData),
  })
}

export function clearPageReuseCache(): void {
  cache.clear()
}
